"use client";
import React, { useEffect, useState, useContext } from "react";
import isBoolean from "lodash/isBoolean";

import { CurrentDeviceContext } from "@stadium/ui/src/contexts/responsive-content";

import { Dropdown, type DropdownProps } from "new-react-bootstrap";
import { type DropdownItemProps } from "new-react-bootstrap/DropdownItem";
import {
  ResponsiveDrawerDropdown,
  DropdownToggle,
  DropdownItem,
} from "@stadium/ui/src/components/bootstrap-dropdown";

import SearchInput, { SearchInputProps } from "@stadium/ui/src/components/form/SearchInput";

import defaultStyles from "./main.module.scss";

export interface SelectOptionProps extends DropdownItemProps {
  value: string;
  text: React.ReactNode;
  id: string;
  selectedValue?: SelectDropdownProps["value"];
  styles?: ModularStyles;
  responsive?: boolean;
  applyDefaultClass?: boolean;
}

export interface SelectDropdownProps {
  options: Array<SelectOptionProps>;
  showSearchFilter?: boolean;
  isSearchClearable?: boolean;
  searchfilterOptions?: Pick<SearchInputProps, "attributes"> | null;
  noOption?: SelectOptionProps;
  name: string;
  onSelect?: (options: {
    name: SelectDropdownProps["name"];
    value: SelectOptionProps["value"];
    selectedValueLabel: React.ReactNode;
  }) => void;
  styles?: ModularStyles;
  /**
   * Make sure to pass only Label instance from ui/src/components/form/Label
   */
  label?: React.ReactNode;
  placeholder?: React.ReactNode;
  /**
   * These classes are appended to the parent container
   */
  className?: string;
  id: string;
  value: string;
  error?: boolean;
  placeholderTextClass?: string;
  menuClass?: string;
  wrapperClassName?: string;
  inputClassName?: string;
  menuClassName?: string;
  applyDefaultItemClass?: boolean;
  /**
   * Selected Value Text Label
   */
  text?: React.ReactNode;
  disabled?: boolean;
  /**
   * Open dropdown upwards
   */
  drop?: DropdownProps["drop"];
  isClearable?: boolean;
  unMountOnExit?: boolean;
  overlayClass?: string;
  toggleClass?: string;
  responsive?: boolean;
  renderOptions?: (options: {
    dropdownOptions: SelectDropdownProps["options"];
    renderOption: (option: SelectOptionProps, ...rest: any[]) => React.ReactNode;
  }) => React.ReactNode | null;
}

const defaultOnSelect = () => "";

const noOptionDefaultValues: SelectOptionProps = {
  text: "No Result",
  className: `text-danger ${defaultStyles.noResult}`,
  value: "",
  id: "no-option",
};

const SelectOption = ({
  id,
  text,
  value,
  selectedValue,
  className = "",
  styles = defaultStyles,
  responsive = false,
  applyDefaultClass = true,
  ...restProps
}: SelectOptionProps) => {
  const { onSelect, ...rest } = restProps as any;

  if (responsive) {
    return (
      <DropdownItem
        key={value}
        value={value}
        isActive={value === selectedValue}
        onClick={(e) => onSelect(value, e)}
        className={styles.dropdownItemResponsive}
        applyDefaultClass={applyDefaultClass}
        {...rest}
      >
        <div className={styles.dropdownItemChild}>{text}</div>
      </DropdownItem>
    );
  }

  return (
    <Dropdown.Item
      {...(Dropdown.Item.defaultProps || {})}
      className={className}
      eventKey={value}
      active={!!selectedValue && selectedValue === value}
      id={`${id}-${value}`}
      bsPrefix={`${styles.dropdownItem} menuListItem`}
      onSelect={onSelect}
      {...rest}
    >
      <div className={styles.dropdownItemChild}>{text}</div>
    </Dropdown.Item>
  );
};

const SelectDropdown = ({
  options,
  showSearchFilter = false,
  isSearchClearable = false,
  searchfilterOptions = null,
  noOption = noOptionDefaultValues,
  onSelect = defaultOnSelect,
  name,
  styles = defaultStyles,
  responsive = false,
  renderOptions: customRenderOptions,
  ...restProps
}: SelectDropdownProps) => {
  const {
    label = null,
    placeholder = "",
    className = "",
    id = `select-dropdown-${Date.now()}`,
    value: selectedValue = "",
    error = false,
    text = "",
    placeholderTextClass = "",
    menuClass = "",
    wrapperClassName = "",
    inputClassName = "",
    disabled = false,
    drop,
    isClearable = false,
    overlayClass = "",
    toggleClass = "",
    menuClassName = "",
    applyDefaultItemClass = true,
    unMountOnExit = false,
  } = restProps;

  const [dropdownOptions, setDropdownOptions] = useState<SelectDropdownProps["options"]>(options);
  const [isVisible, setIsVisible] = useState(false);

  const { isMobileDevice } = useContext(CurrentDeviceContext);

  const onSelectOption = (
    eventKey: string,
    e: React.SyntheticEvent<unknown, Event>,
    selectedLabel: React.ReactNode,
  ) => {
    onSelect({ name, value: eventKey, selectedValueLabel: selectedLabel });
  };

  const clearSelectedOption = (e) => {
    e.preventDefault();
    onSelectOption(null, e, null);
  };

  useEffect(() => {
    setDropdownOptions(options);
  }, [options]);

  const renderSearchFilter = () => {
    if (showSearchFilter) {
      return (
        <SearchInput
          id={`${id}SearchFilter`}
          name={`${name}SearchFilter`}
          {...searchfilterOptions}
          data={options}
          onFilter={(filteredOptions) => setDropdownOptions(filteredOptions)}
          isSearchClearable={isSearchClearable}
          reset={!isVisible}
          focus={isVisible}
          styles={styles !== defaultStyles ? styles : undefined}
          classes={wrapperClassName}
          className={inputClassName}
          addTopAlignClass
        />
      );
    }
  };

  const renderOptions = () => {
    if (dropdownOptions.length) {
      if (customRenderOptions)
        return customRenderOptions({
          dropdownOptions,
          renderOption: ({ option, ...rest }) => (
            <SelectOption
              key={`${id}-${option.value}`}
              id={id}
              onSelect={(key, e) => onSelectOption(key, e, option.text)}
              text={option.text}
              value={option.value}
              selectedValue={selectedValue}
              styles={styles !== defaultStyles ? styles : undefined}
              responsive={responsive}
              applyDefaultClass={applyDefaultItemClass}
              {...rest}
            />
          ),
        });

      return dropdownOptions.map(({ text: optionText, value: optionValue }) => (
        <SelectOption
          key={`${id}-${optionValue}`}
          id={id}
          onSelect={(key, e) => onSelectOption(key, e, optionText)}
          text={optionText}
          value={optionValue}
          selectedValue={selectedValue}
          styles={styles !== defaultStyles ? styles : undefined}
          responsive={responsive}
          className={styles.plainList}
          applyDefaultClass={applyDefaultItemClass}
        />
      ));
    }
    return <SelectOption {...noOption} styles={styles !== defaultStyles ? styles : undefined} />;
  };

  const onToggle = (isOpen) => {
    if (isOpen) {
      setIsVisible(isOpen);
    } else {
      setIsVisible(isOpen);
      setDropdownOptions(options);
    }
  };

  const dropdownClassNames = `${error ? styles.formInputError : ""}`;

  // even if the value of the selected item is false, the item is still selected
  const isSelected = selectedValue || isBoolean(selectedValue);
  const dropdownToggleClassNames = `${styles.dropdownValue} ${
    isSelected ? "selectedState" : `${styles.placeholderText} ${placeholderTextClass}`
  }`;

  if (responsive) {
    return (
      <>
        {React.isValidElement(label) &&
          React.cloneElement(label, { disabled, htmlFor: id, "aria-disabled": disabled })}
        <div className={`${className} ${styles.dropdown}`}>
          <ResponsiveDrawerDropdown
            onToggle={onToggle}
            enablePortal={isMobileDevice}
            label={
              <DropdownToggle disabled={disabled}>
                <div>{isSelected ? text : placeholder}</div>
              </DropdownToggle>
            }
            menuClassName={menuClassName}
          >
            {(!unMountOnExit || isVisible) && (
              <>
                {renderSearchFilter()}
                {renderOptions()}
              </>
            )}
          </ResponsiveDrawerDropdown>
        </div>
      </>
    );
  }

  return (
    <>
      {React.isValidElement(label) &&
        React.cloneElement(label, { disabled, htmlFor: id, "aria-disabled": disabled })}
      <div className={`${className} ${styles.dropdown}`}>
        <Dropdown
          {...(Dropdown.defaultProps || {})}
          id={id}
          className={dropdownClassNames}
          onToggle={onToggle}
          drop={drop}
        >
          <Dropdown.Toggle
            {...(Dropdown.Toggle.defaultProps || {})}
            disabled={disabled}
            variant="block"
            bsPrefix={`${styles.dropdownToggle} ${
              isSelected
                ? "selectStateValue"
                : `${styles.notSelectedStateValue} ${placeholderTextClass}`
            } ${toggleClass}`}
          >
            <div className={dropdownToggleClassNames}>{isSelected ? text : placeholder}</div>
          </Dropdown.Toggle>
          <div className={overlayClass}></div>
          <Dropdown.Menu
            {...(Dropdown.Menu.defaultProps || {})}
            bsPrefix={styles.dropdownMenu}
            className={menuClass}
            renderOnMount
          >
            {!unMountOnExit || isVisible ? (
              <>
                {renderSearchFilter()}
                {renderOptions()}
              </>
            ) : null}
          </Dropdown.Menu>
          {isClearable && (
            <div>
              {isSelected ? (
                <button
                  type="button"
                  onClick={(e) => clearSelectedOption(e)}
                  className={`${styles.closeBtn} ${isSelected ? "onlyCloseIcon" : ""}`}
                ></button>
              ) : (
                ""
              )}
            </div>
          )}
        </Dropdown>
      </div>
    </>
  );
};

export default SelectDropdown;
